/*
  Introduce tiptap extension for text indentation.

  As tiptap extensions do not include text indentation formatting, we will need to create our own implementation.
  This class implements the text indentation extension which supports indent and outdent.

  An implementation example,

  ```js
  <el-button
    type="text"
    @click="commands.indentation({ indent: true })"
  >Indent</el-button>
  <el-button
    type="text"
    @click="commands.indentation({ indent: false })"
  >Outdent</el-button>
  ```

  Note that we will never apply `is-active` class for text indentation as toggling does not apply here.

  The plugin is inspired by https://github.com/ueberdosis/tiptap/issues/360 and
  tiptap-extensions/src/nodes/Blockquote.js.
 */
import {Node} from 'tiptap'
import {lift, wrapIn} from 'tiptap-commands'
import {isNil} from 'lodash'

function setIdentOutdent (type, attrs = {}) {
  // This is a function based of toggleWrap from tiptap-commands.
  // Instead of toggling the formatting based on active status, we would like to indent or outdent
  // based on the indent attribute.
  return (state, dispatch, view) => {
    if (isNil(attrs.indent)) {
      // do nothing
      return false
    }

    if (attrs.indent) {
      // outdent
      return wrapIn(type, attrs)(state, dispatch, view)
    }

    // indent
    return lift(state, dispatch)
  }
}

export default class Indentation extends Node {
  get name () {
    return 'indentation'
  }

  get schema () {
    return {
      attrs: {
        indent: {
          default: true
        }
      },
      content: 'block*',
      group: 'block',
      defining: true,
      draggable: false,
      parseDOM: [{
        // Set higher priority than the blockquote node, with default priority value of 50.
        priority: 51,
        tag: 'blockquote',
        getAttrs: node => node.classList.length === 1 && node.classList.contains('indent') ? {indent: true} : false
      }],
      toDOM: node => {
        return ['blockquote', {class: node.attrs.indent ? 'indent' : ''}, 0]
      }
    }
  }

  commands ({type}) {
    return attrs => setIdentOutdent(type, attrs)
  }

  keys ({type}) {
    return {
      'Tab': setIdentOutdent(type, {indent: true}),
      'Shift-Tab': setIdentOutdent(type, {indent: false})
    }
  }
}
